import React, { useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import "../../App.css";
import "./Contact.css";
import API_BASE_URL from "../../api";

const Contact = () => {
    const [name, setName]                 = useState("");
    const [email, setEmail]               = useState("");
    const [phone, setPhone]               = useState("");
    const [message, setMessage]           = useState("");
    const [privacyAgreed, setPrivacyAgreed] = useState(false);

    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError]     = useState("");

    const { executeRecaptcha } = useGoogleReCaptcha();

    const address  = "2 Little Herdhill Way, Kirriemuir, Angus, Scotland, DD8 5JZ";
    const emailAddr = "contact@ploughbreakers.co.uk";
    const mapSrc   = `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");

        if (!email && !phone) {
            setError("Please provide at least one contact method — email or phone.");
            return;
        }

        if (!privacyAgreed) {
            setError("Please agree to the Privacy Policy before submitting.");
            return;
        }

        if (!executeRecaptcha) {
            setError("reCAPTCHA not ready. Please try again.");
            return;
        }

        try {
            setLoading(true);
            const token = await executeRecaptcha('contact_form');
            await axios.post(`${API_BASE_URL}/api/contact`, {
                name,
                email,
                phone,
                message,
                recaptchaToken: token,
            });

            setSuccess("Thanks — we've received your message.");
            setName("");
            setEmail("");
            setPhone("");
            setMessage("");
            setPrivacyAgreed(false);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to send message. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [executeRecaptcha, name, email, phone, message, privacyAgreed]);

    return (
        <div className="contact-page">
            {/* FULL WIDTH MAP */}
            <div className="contact-map-wrap">
                <iframe
                    title="Charlton Farm Services Location"
                    src={mapSrc}
                    className="contact-map"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                />
            </div>

            <div className="contact-content">
                <h1>Contact Charlton Farm Services</h1>
                <p className="contact-intro">
                    Have a question about machinery, plough parts, or plant hire? Get in touch and we'll reply as soon as possible.
                </p>

                {/* CONTACT CARD */}
                <div className="contact-card">
                    <div className="contact-card-left">
                        <h3 className="contact-card-title">Contact details</h3>

                        <div className="contact-detail">
                            <div className="contact-detail-label">Address</div>
                            <div className="contact-detail-value">{address}</div>
                        </div>

                        <div className="contact-detail">
                            <div className="contact-detail-label">Email</div>
                            <a className="contact-detail-link" href={`mailto:${emailAddr}`}>
                                {emailAddr}
                            </a>
                        </div>

                        <div className="contact-detail">
                            <div className="contact-detail-label">Phone</div>
                            <div className="contact-detail-value">
                                <strong>Ernest:</strong>{" "}
                                <a className="contact-detail-link" href="tel:07970506155">
                                    07970 506155
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="contact-card-right">
                        <div className="contact-actions">
                            <a className="contact-page-btn contact-page-btn-call" href="tel:07970506155">
                                Call 07970 506155
                            </a>
                            <a className="contact-page-btn contact-page-btn-whatsapp" href="https://wa.me/" target="_blank" rel="noopener noreferrer">
                                <i className="fab fa-whatsapp" style={{ marginRight: '8px', fontSize: '1.1rem' }} />
                                WhatsApp Enquiry
                            </a>
                        </div>
                    </div>
                </div>

                {error   && <p className="contact-error">{error}</p>}
                {success && <p className="contact-success">{success}</p>}

                <h2>Send us a message</h2>

                <form className="contact-form" onSubmit={handleSubmit}>
                    <label>
                        Name *
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </label>

                    <div className="contact-form-row">
                        <label>
                            Email
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your@email.com"
                            />
                        </label>
                        <label>
                            Phone
                            <input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="07700 000000"
                            />
                        </label>
                    </div>
                    <p className="contact-form-hint">* At least one contact method required</p>

                    <label>
                        Message *
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={6}
                            required
                        />
                    </label>

                    <label className="contact-privacy-check">
                        <input
                            type="checkbox"
                            checked={privacyAgreed}
                            onChange={(e) => setPrivacyAgreed(e.target.checked)}
                            required
                        />
                        <span>
                            I have read and agree to the{" "}
                            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                        </span>
                    </label>

                    <button type="submit" disabled={loading}>
                        {loading ? "Sending..." : "Send Message"}
                    </button>

                    <p className="contact-privacy-note">
                        By submitting this form you agree to our{" "}
                        <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
                    </p>
                </form>

                <div className="contact-back">
                    <Link to="/">← Back to Home</Link>
                </div>
            </div>
        </div>
    );
};

export default Contact;